import { useNavigate, useSearch } from "@tanstack/react-router";
import { useMe } from "../../api/queries";
import { AboutBox } from "../../design/components/AboutBox";
import { Reason } from "../../design/components/Reason";
import { Segmented } from "../../design/components/Segmented";
import { SettingsLayout } from "../../design/components/SettingsLayout";
import { useT } from "../../i18n/locale";
import { useAccess, useMenuSubject } from "../../session/access";
import { PlatformCatalogTab } from "./platform-catalog";
import { RolesEditorTab } from "./roles-editor";
import { UsersTab } from "./roles-users";

// Impostazioni → Ruoli e permessi (piano M1b Task 8, mock-up `permessi.html`): tre schede, «Utenti», «Ruoli» e
// «Per tutte le aziende» (soltanto per il Super admin di Gioia365). La scheda sta nell'indirizzo, così si
// condivide e il pulsante «indietro» funziona: `?scheda=utenti|ruoli|piattaforma`. «Ruolo su misura» porta a
// `?scheda=ruoli&suMisura=<appartenenza>`: l'editor dei ruoli (`roles-editor.tsx`) apre la bozza per quella persona
// e toglie il parametro dall'indirizzo.

const TABS = ["utenti", "ruoli", "piattaforma"] as const;
export type RolesTab = (typeof TABS)[number];
export type RolesSearch = { scheda?: RolesTab; suMisura?: string };

/** L'indirizzo è di chi lo scrive: una scheda sconosciuta o un valore che non è testo si ignorano. */
export function validateRolesSearch(search: Record<string, unknown>): RolesSearch {
  const scheda = TABS.find((candidate) => candidate === search.scheda);
  return {
    ...(scheda ? { scheda } : {}),
    ...(typeof search.suMisura === "string" && search.suMisura !== "" ? { suMisura: search.suMisura } : {}),
  };
}

export function RolesPage() {
  return (
    <SettingsLayout section="roles">
      <RolesSection />
    </SettingsLayout>
  );
}

function RolesSection() {
  const t = useT();
  const access = useAccess();
  const me = useMe();
  const { observed } = useMenuSubject();
  const navigate = useNavigate();
  const search = useSearch({ from: "/autenticato/guscio/azienda/impostazioni/ruoli-e-permessi" });
  const organization = access.context.organization.name;
  // «Per tutte le aziende» esiste soltanto per il Super admin di Gioia365, e mai nella Vista Commerciale, dove il menu è
  // quello del Commerciale e non ha funzioni della piattaforma (decisione del 2 ottobre 2026); un indirizzo che la chiede
  // a chi non la ha apre «Utenti».
  const visible = TABS.filter((candidate) => candidate !== "piattaforma" || (me.isPlatformAdmin && !observed));
  const requested = search.scheda ?? "utenti";
  const current = visible.find((candidate) => candidate === requested) ?? "utenti";
  const labels: Record<RolesTab, string> = {
    utenti: t("roles.tabs.users"),
    ruoli: t("roles.tabs.roles"),
    piattaforma: t("roles.tabs.platform"),
  };

  function choose(value: string) {
    const next = visible.find((candidate) => candidate === value);
    if (next) void navigate({ to: "/impostazioni/ruoli-e-permessi", search: { scheda: next } });
  }

  return (
    <>
      <AboutBox title={t("roles.about.title")}>
        <p>{t("roles.about.text", { organization })}</p>
        <h2>{t("roles.about.whoDecidesTitle")}</h2>
        <p>{t("roles.about.whoDecides")}</p>
      </AboutBox>
      <Segmented
        label={t("roles.tabs.label")}
        options={visible.map((candidate) => ({ value: candidate, label: labels[candidate] }))}
        value={current}
        onChange={choose}
      />
      {access.can("manage_roles") ? null : <Reason>{t("roles.readOnly")}</Reason>}
      {current === "utenti" ? <UsersTab /> : null}
      {current === "ruoli" ? <RolesEditorTab suMisura={search.suMisura} /> : null}
      {current === "piattaforma" ? <PlatformCatalogTab /> : null}
    </>
  );
}
